/**
 * Pure formatters for the eval runs table, split out so the defensive reads of
 * the opaque `config` / `aggregates` JSON blobs are unit-testable without
 * rendering the table. The DE proxy passes both blobs through as untyped JSON,
 * so each reader narrows at runtime: a malformed or in-flight run (an object
 * where a string is expected, a non-finite score, a missing key) degrades to a
 * placeholder instead of crashing the table.
 */
import { isNil } from "lodash";

/** A run's model id (DE `config.modelId`), or null when absent/malformed. */
export const modelIdOf = (config: unknown): string | null => {
  const value = (config as { modelId?: unknown } | null)?.modelId;
  return typeof value === "string" ? value : null;
};

/**
 * A run's reasoning effort (DE `config.reasoningEffort`), or null when it is
 * absent, null, empty, or malformed — all of which mean "the provider default".
 */
export const reasoningEffortOf = (config: unknown): string | null => {
  const value = (config as { reasoningEffort?: unknown } | null)
    ?.reasoningEffort;
  return typeof value === "string" && value !== "" ? value : null;
};

/**
 * The headline all-fields entity accuracy (`aggregates.tiers.all.microEntity`,
 * the same value the run detail page's "All fields" tier card shows), or null
 * until the run is scored / when the aggregates are malformed.
 */
export const avgScorePct = (aggregates: unknown): number | null => {
  const value = (
    aggregates as { tiers?: { all?: { microEntity?: unknown } } } | null
  )?.tiers?.all?.microEntity;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

export const formatPct = (value: number | null): string =>
  value === null ? "—" : `${value.toFixed(1)}%`;

/** A null/absent reasoning effort renders as the provider default. */
export const formatEffort = (effort: string | null): string =>
  isNil(effort) ? "default" : effort;
